import { Breadcrumbs, IconButton, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import KenColors from '../../Helpers/Themes/kenColors';
import { getStudentInfo } from "../../Utils/apiService";
import KenLoader from '../../GlobalComponents/KenLoader';
import { getUserDetails } from "../../storageService";
import routes from '../../Utils/routes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const useStyles = makeStyles((theme: Theme) => ({
  mainText: {
    fontWeight: '600 !important',
    textDecoration: 'none',
    textTransform: 'capitalize',
    margin: '0px !important',
    padding: '0px !important',
    '@media (min-width: 1280px) and (max-width: 1300px)': {
      fontSize: '13px !important',
    },
  },
  listText: {
    textDecoration: 'none',
    textTransform: 'capitalize',
    margin: '0px !important',
    padding: '0px !important',
  },
}));

const AppBreadCrumbs: React.FunctionComponent = () => {
  const { pathname, state } = useLocation();
  console.log("Current Path:", pathname);
  const classes = useStyles();
  const [studentInfo, setStudentInfo] = useState({
    fullName: ""
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userDetails = getUserDetails();
    if (userDetails?.ContactId) {
      getStudentInfo(userDetails.ContactId)
        .then((data) => {
          setStudentInfo({
            fullName: `${data?.firstName} ${data?.lastName}`,
          });
        })
        .catch(() => setError("Failed to load student details."))
        .finally(() => setLoading(false));
    }
  }, []);

  if (loading) return <KenLoader />;
  if (error) return <Typography color="error">{error}</Typography>;

  // Process the current path
  const rawPath = decodeURIComponent(pathname);
  let navigateHistory = rawPath.split('/').filter(item => item && item !== '');

  // Special case: If on Home or My Profile page, show custom greeting
  if (pathname.toLowerCase().replace(/\s+/g, '') === '/home' || pathname.toLowerCase().replace(/\s+/g, '') === '/my%20Profile') {
    return (
      <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={'1rem'}>
        Hi {studentInfo.fullName}, have a Great day!
      </Typography>
    );
  }

  // Clean up breadcrumb items - remove ID parts
  const cleanBreadcrumbs = [...navigateHistory];
  const originalFullPath = [...navigateHistory];

  // Simple ID pattern detection (assumes IDs follow specific named segments)
  for (let i = 0; i < cleanBreadcrumbs.length; i++) {
    const current = cleanBreadcrumbs[i];
    const prev = i > 0 ? cleanBreadcrumbs[i - 1] : '';

    // Check if the current item looks like an ID (after specific segments)
    const isIDAfterSpecificSegment =
      (prev === 'Student Details' ||
        prev === 'Student Info' ||
        prev === 'Session Attendance') &&
      (/^[a-zA-Z0-9-]+$/.test(current));

    if (isIDAfterSpecificSegment) {
      // Remove the ID from display but keep track of the original path
      cleanBreadcrumbs.splice(i, 1);
      i--; // Adjust index since we removed an element
    }
  }

  const isResourcesPath = pathname.endsWith('/Resources');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isResourcesPath && (
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography className={classes.mainText} color={KenColors.ClientBlack2} fontSize={'0.9375rem'}>
          {cleanBreadcrumbs[0]}
        </Typography>
      </div>
      {cleanBreadcrumbs.length > 1 && (
        <Breadcrumbs separator={<Typography sx={{ fontSize: '0.75rem', fontWeight: '500' }}>&gt;</Typography>} style={{ color: '#3061FF' }}>
          {cleanBreadcrumbs.map((item, index) => {
            const isLastIndex = index === cleanBreadcrumbs.length - 1;

            // Build path up to this segment (preserving IDs in the actual path)
            let currentPathSegments = [];
            let segmentCounter = 0;

            // This simpler approach rebuilds the path including IDs
            for (let i = 0; i < originalFullPath.length; i++) {
              currentPathSegments.push(originalFullPath[i]);

              // If this segment matches our current breadcrumb and we've found enough matches
              if (originalFullPath[i] === item && segmentCounter === index) {
                break;
              }

              // Only count non-ID segments for matching against our clean breadcrumb
              const prev = i > 0 ? originalFullPath[i - 1] : '';
              const isIDSegment =
                (prev === 'Student Details' ||
                  prev === 'Student Info' ||
                  prev === 'Session Attendance') &&
                (/^[a-zA-Z0-9-]+$/.test(originalFullPath[i]));

              if (!isIDSegment) {
                segmentCounter++;
              }
            }

            // Build the path including any IDs
            const newPath = '/' + currentPathSegments.join('/');
            let displayItem = item.replace(/-/g, ' ');

            return index === cleanBreadcrumbs.length - 1 && state?.breadCrumbs ? (
              <Typography key={index} className={classes.listText} color={'#193389'} fontSize={'0.75rem'}>
                {state.breadCrumbs}
              </Typography>
            ) : (
              <Link key={index} className={classes.listText} to={newPath}>
                <Typography
                  sx={{
                    color: isLastIndex ? KenColors.ClientBlack2 : '#3061FF',
                    fontWeight: isLastIndex ? '400' : '600',
                    fontSize: '0.75rem'
                  }}
                >
                  {displayItem}
                </Typography>
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
    </>
  );
};

export default AppBreadCrumbs;